// @flow strict

import React, { useState } from 'react';
import { Button, Container } from '@material-ui/core';
import { MenuProducts } from './menuProducts.js';
import { MenuCustomers } from './menuCustomers.js';
import { MenuCompany } from './menuCompany.js';
import { useLanguageContext } from '~contexts/language-context';
import { createLinkPath } from '~utils/helpers/create-link-path';
import { graphql, useStaticQuery } from 'gatsby';

import * as styles from './navigation.module.less';
import { LanguageSwitch } from '../../language-switch/language-switch.js';

export const Navigation = ({ alternates, showBlogLink }) => {
  const { loc, language } = useLanguageContext();

  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const [isCustomersMenuOpen, setIsCustomersMenuOpen] = useState(false);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);

  const data = useStaticQuery(graphql`
    query NavigationQuery {
      allNavigationJson {
        nodes {
          item
          name
          language
          items {
            name
            text
            badge
            href
            highlight
            image {
              src {
                publicURL
              }
              alt
            }
          }
          teamImage {
            src {
              publicURL
            }
            alt
          }
          ctaParagraph {
            headline
            text
          }
          ctaButton {
            text
            href
          }
          benefits
          links {
            name
            href
          }
          href
          industries {
            title
            href
            iconSrc {
              publicURL
            }
            iconAlt
          }
        }
      }
    }
  `);

  const menuItemsProducts = data.allNavigationJson.nodes.find(
    (menuItem) =>
      menuItem.item === 'Products' && menuItem.language === language,
  );

  const menuItemsCustomers = data.allNavigationJson.nodes.find(
    (menuItem) =>
      menuItem.item === 'Customers' && menuItem.language === language,
  );

  const menuItemsCompany = data.allNavigationJson.nodes.find(
    (menuItem) => menuItem.item === 'Company' && menuItem.language === language,
  );

  const menuItemsPricing = data.allNavigationJson.nodes.find(
    (menuItem) => menuItem.item === 'Pricing' && menuItem.language === language,
  );

  const openProductsMenu = () => {
    setIsProductsMenuOpen(true);
  };

  const closeProductsMenu = () => {
    setIsProductsMenuOpen(false);
  };

  const openCustomersMenu = () => {
    setIsCustomersMenuOpen(true);
  };

  const closeCustomersMenu = () => {
    setIsCustomersMenuOpen(false);
  };

  const openCompanyMenu = () => {
    setIsCompanyMenuOpen(true);
  };

  const closeCompanyMenu = () => {
    setIsCompanyMenuOpen(false);
  };

  return (
    <>
      <div className={styles.banner}>
        <div className={styles.gradientLeft}></div>
        {language === 'en' && (
          <div className={styles.bannerCtaWrapper}>
            <p className={styles.bannerCtaText}>
              NEW: <span style="color: #f60">OMQ</span>´s beta chatbot on{' '}
              <span style="color: #18A57E">ChatGPT / GPT-4 </span> level
            </p>
            <a
              className={styles.bannerCtaButton}
              href="https://omq.ai/chatgpt-chatbot/"
            >
              To the chatbot
            </a>
          </div>
        )}
        {language === 'de' && (
          <div className={styles.bannerCtaWrapper}>
            <p className={styles.bannerCtaText}>
              Jetzt verfügbar: <span style="color: #f60">OMQ</span>´s Chatbot
              auf <span style="color: #18A57E">ChatGPT / GPT-4</span> Level
            </p>
            <a
              className={styles.bannerCtaButton}
              href="https://omq.ai/de/chatgpt-chatbot/"
            >
              Zum Chatbot
            </a>
          </div>
        )}
        <div className={styles.gradientRight}></div>
      </div>
      <nav className={styles.mainNavigation}>
        <Container>
          <div className={styles.wrapper}>
            <p className={styles.companyLink}>
              Magazine by{' '}
              <a href={createLinkPath({ path: '/', language })}>OMQ</a>
            </p>
            <ul className={styles.navItems}>
              <li
                onMouseEnter={openProductsMenu}
                onMouseLeave={closeProductsMenu}
              >
                {menuItemsProducts.name}
              </li>
              <li
                onMouseEnter={openCustomersMenu}
                onMouseLeave={closeCustomersMenu}
              >
                {menuItemsCustomers.name}
              </li>
              <li
                onMouseEnter={openCompanyMenu}
                onMouseLeave={closeCompanyMenu}
              >
                {menuItemsCompany.name}
              </li>
              <li className={styles.plainLink}>
                <a href={menuItemsPricing.href}>{menuItemsPricing.name}</a>
              </li>
              {showBlogLink && (
                <li className={styles.plainLink}>
                  <a href={createLinkPath({ path: '/blog', language })}>Blog</a>
                </li>
              )}
            </ul>

            {isProductsMenuOpen && (
              <div
                onMouseEnter={openProductsMenu}
                onMouseLeave={closeProductsMenu}
                className={styles.menuWrapper}
              >
                <div className={styles.menu}>
                  <Container>
                    <MenuProducts data={menuItemsProducts} />
                  </Container>
                </div>
              </div>
            )}

            {isCustomersMenuOpen && (
              <div
                onMouseEnter={openCustomersMenu}
                onMouseLeave={closeCustomersMenu}
                className={styles.menuWrapper}
              >
                <div className={styles.menu}>
                  <Container>
                    <MenuCustomers data={menuItemsCustomers} />
                  </Container>
                </div>
              </div>
            )}

            {isCompanyMenuOpen && (
              <div
                onMouseEnter={openCompanyMenu}
                onMouseLeave={closeCompanyMenu}
                className={styles.menuWrapper}
              >
                <div className={styles.menu}>
                  <Container>
                    <MenuCompany data={menuItemsCompany} />
                  </Container>
                </div>
              </div>
            )}

            {/* Language switch */}
            {alternates && (
              <div className={styles.languageSwitch}>
                <LanguageSwitch alternates={alternates} />
              </div>
            )}

            <Button
              href={loc('_links_navigation_cta_button')}
              size="small"
              variant="contained"
              classes={{
                root: styles.buttonNavBar,
                label: styles.buttonLabel,
              }}
            >
              {loc('_navigation_cta_button')}
              <svg
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.21127 14.7891L1 13.5779L6.57746 8.0004L1 2.42294L2.21127 1.21167L9 8.0004L2.21127 14.7891Z"
                  stroke-width="0.5"
                  stroke-linejoin="round"
                />
              </svg>
            </Button>
          </div>
        </Container>
      </nav>
    </>
  );
};
